/**
 * request-events — typed one-way event channel from the request layer to the
 * app shell. (WarpDrive migration ADR-001.)
 *
 * The network layer never calls UI or session APIs (no toasts, no
 * session.invalidate, no Datadog): it EMITS, and each app's shell subscribes
 * and decides what an event means for it. This replaces the per-app
 * `response.*` callback bag — and with it, the construction-time capture of
 * service closures that made store setup order load-bearing.
 *
 * Framework-free on purpose: no Ember imports, no DI, no owner.
 */

export type TioRequestEvent =
  | {
      /** A response status the app considers session-fatal (401, 403…). */
      type: 'unauthorized';
      status: number;
      url?: string;
      method: string;
      payload?: unknown;
      userId?: string;
      /** Milliseconds from dispatch to the failure, when the handler measured it. */
      duration?: number;
    }
  | {
      /** A non-fatal but user-relevant failure (e.g. 400/422 with detail). */
      type: 'request-failed';
      status: number;
      url?: string;
      method: string;
      /** Human-readable message already extracted/prefixed by the handler. */
      message: string;
      payload?: unknown;
      userId?: string;
      /** Milliseconds from dispatch to the failure, when the handler measured it. */
      duration?: number;
    };

type Listener = (event: TioRequestEvent) => void;

const listeners = new Set<Listener>();

/** Subscribe; returns an unsubscribe function (call it on teardown). */
export function onRequestEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitRequestEvent(event: TioRequestEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch (error) {
      // A broken subscriber must never break the request layer.
      console.error('request-events listener threw', error);
    }
  }
}

/** Test helper. */
export function resetRequestEvents(): void {
  listeners.clear();
}
